/**
 * RaroSiteRouter.jsx — hosted at raro.au (and mirrors). Maps CLEAN paths
 * (no /raro prefix) to the Raro page components:
 *
 *   /              → RaroLandingPage (icon grid — the multi-purpose QR lander)
 *   /takeaway      → RaroTakeawayPage
 *   /table/:tableNo→ RaroTablePage (patron tabletop QR scan target)
 *   /pos           → RaroPosPage
 *   /admin         → RaroAdminPage
 *   /contact       → RaroContactPage
 *
 * /menu + /book were removed (2026-09-25) — will be rebuilt later from
 * token data.  Both redirect to `/` for now.
 *
 * Anything else falls back to the landing.  The platform-scoped
 * /raro/* paths in App.js still work when developing on the pod URL.
 */
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { RaroBrandProvider } from "./RaroBrandContext";
import RaroOwnerBar from "./RaroOwnerBar";

const RaroLandingPage    = lazy(() => import("@/pages/raro/RaroLandingPage"));
const RaroMenuPage       = lazy(() => import("@/pages/raro/RaroMenuPage"));
const RaroTablePage      = lazy(() => import("@/pages/raro/RaroTablePage"));
const RaroTakeawayPage   = lazy(() => import("@/pages/raro/RaroTakeawayPage"));
const RaroPosPage        = lazy(() => import("@/pages/raro/RaroPosPage"));
const RaroAdminPage      = lazy(() => import("@/pages/raro/RaroAdminPage"));
const RaroContactPage    = lazy(() => import("@/pages/raro/RaroContactPage"));
const RaroAreaGuidePage  = lazy(() => import("@/pages/raro/RaroAreaGuidePage"));
const RaroMenuItemPage   = lazy(() => import("@/pages/raro/RaroMenuItemPage"));
const RaroPaySuccessPage = lazy(() => import("@/pages/raro/RaroPaySuccessPage"));
const RaroBookingPage    = lazy(() => import("@/pages/raro/RaroBookingPage"));
const RaroWaitlistClaimPage = lazy(() => import("@/pages/raro/RaroWaitlistClaimPage"));
const RaroStaffLoginPage    = lazy(() => import("@/pages/raro/RaroStaffLoginPage"));
const RaroStaffTerminalPage = lazy(() => import("@/pages/raro/RaroStaffTerminalPage"));
const RaroStaffKitchenPage  = lazy(() => import("@/pages/raro/RaroStaffKitchenPage"));
const RaroStaffSettingsPage = lazy(() => import("@/pages/raro/RaroStaffSettingsPage"));
const RaroStaffAuditPage    = lazy(() => import("@/pages/raro/RaroStaffAuditPage"));
const RaroStaffClosePage    = lazy(() => import("@/pages/raro/RaroStaffClosePage"));
const RaroStaffQRPage       = lazy(() => import("@/pages/raro/RaroStaffQRPage"));
const FloorPlanDesigner     = lazy(() => import("@/pages/eats/FloorPlanDesigner"));

export default function RaroSiteRouter() {
  return (
    <RaroBrandProvider>
      <RaroOwnerBar />
      <Suspense fallback={<div style={{ minHeight: "100vh", background: "#fdf6ec" }} />}>
        <Routes>
          <Route path="/"                element={<RaroLandingPage />} />
          <Route path="/menu"            element={<RaroMenuPage />} />
          <Route path="/menu/:slug"      element={<RaroMenuItemPage />} />
          <Route path="/book"            element={<RaroBookingPage />} />
          <Route path="/waitlist/claim/:wid" element={<RaroWaitlistClaimPage />} />
          <Route path="/staff/login"     element={<RaroStaffLoginPage />} />
          <Route path="/staff"           element={<RaroStaffTerminalPage />} />
          <Route path="/staff/kitchen"   element={<RaroStaffKitchenPage />} />
          <Route path="/staff/settings"  element={<RaroStaffSettingsPage />} />
          <Route path="/staff/audit"     element={<RaroStaffAuditPage />} />
          <Route path="/staff/close"     element={<RaroStaffClosePage />} />
          <Route path="/staff/qr"        element={<RaroStaffQRPage />} />
          <Route path="/staff/floorplan" element={<FloorPlanDesigner />} />
          <Route path="/takeaway"        element={<RaroTakeawayPage />} />
          <Route path="/pay-success"    element={<RaroPaySuccessPage />} />
          <Route path="/table/:tableNo/seat/:seatNo" element={<RaroMenuPage />} />
          <Route path="/table/:tableNo"  element={<RaroTablePage />} />
          <Route path="/pos"             element={<RaroPosPage />} />
          {/* Thin venue settings page relocated to /venue-admin — the main
              {domain}/admin now opens the full HustlePlate operator console
              (see RaroHostGate in App.js). Nothing removed. */}
          <Route path="/venue-admin"     element={<RaroAdminPage />} />
          <Route path="/contact"         element={<RaroContactPage />} />
          <Route path="/area/:slug"      element={<RaroAreaGuidePage />} />
          <Route path="/loyalty"         element={<RaroLandingPage />} />
          <Route path="*"                element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </RaroBrandProvider>
  );
}
